/**
 * The door.
 *
 * Reached only by people who arrive at `/call/CODE` without a grant — a shared
 * link, or a reload after the token aged out. Anyone who came through the
 * lobby already gave their name and already saw themselves, and sending them
 * through this a second time would be an insult.
 *
 * It is the same `MediaPreview` the lobby uses. One component, so the two
 * cannot drift into looking like different products.
 */

import * as React from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { formatJoinCode } from '@era/shared'
import type { PreflightMedia } from '@/lib/livekit-handoff'
import { cn } from '@/lib/utils'
import { MediaPreview } from './components/media-preview'
import { CallKeyframes } from './components/call-styles'
import { SAFE_PAD_DOOR } from './safe-area'
import { usePreview } from './use-preview'

/**
 * Whether the primary pointer is a finger. Read once, at mount — it does not
 * change under anyone during a join.
 *
 * `pointer: coarse` and not a width breakpoint, for the reason recorded in
 * `styles.css`: an iPhone in landscape is 844px wide and sails over every
 * phone breakpoint, so a width test gets the device wrong on exactly the
 * device it exists for.
 */
function useTouchPrimary(): boolean {
  return React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
  )[0]
}

export function Prejoin({
  code,
  title,
  host = null,
  initialName,
  busy,
  error,
  onJoin,
}: {
  code: string
  title: string | null
  /**
   * Who created the room, when the code has been resolved. Optional because
   * the door still renders if `GET /api/rooms/:code` could not be reached —
   * that failure is about this server, not about the visitor's link.
   */
  host?: string | null
  initialName: string
  busy: boolean
  error: { title: string; detail: string } | null
  onJoin: (displayName: string, preflight: PreflightMedia | null) => void
}) {
  const preview = usePreview()
  const touch = useTouchPrimary()
  const [name, setName] = React.useState(initialName)
  const trimmed = name.trim()
  const canJoin = trimmed.length > 0 && !busy

  // A failed join means nobody took the tracks after all. Reclaiming them
  // restores this component as their owner, so leaving the page still closes
  // the camera instead of stranding it open with no UI attached.
  const { reclaim } = preview
  React.useEffect(() => {
    if (error) reclaim()
  }, [error, reclaim])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!canJoin) return
    // Hand the live tracks over BEFORE navigating into the call, so the camera
    // never closes. After this call the preview no longer owns them.
    onJoin(trimmed, preview.handOff())
  }

  return (
    /* `h-full` + one scroller, not `min-h-[100dvh]`.
       `#root` is already exactly the viewport, so `min-h-[100dvh]` here could
       only ever equal it or exceed it — and when it exceeded it the surplus was
       not scrolled past, it was CLIPPED, because the document does not scroll.
       The join button is the last thing in this column, so the thing being cut
       off was the only control on the screen.

       Everything below is sized so that on the tightest phone still shipping —
       iPhone SE at 375x667 — the preview, the name field and the join button
       are all above the fold and this scroller never has anything to do. It
       stays because a 40-character room title, a long error and a landscape
       phone can each still overflow, and clipping is not an acceptable answer
       for a button. */
    <div className="h-full overflow-y-auto overscroll-contain bg-[#0b0a09] text-[#f3eee8]">
      <CallKeyframes />

      <div className={cn('grid min-h-full place-items-center', SAFE_PAD_DOOR)}>
        {/* Two columns from `sm` rather than `lg`.
            A phone in landscape is 844x390: at `lg` this stacked the camera
            preview above the form, which put the join button roughly 400px
            below the fold of a 390px-high screen. Side by side, the tallest
            column is the preview at ~290px and the whole door fits. */}
        <div className="mx-auto grid w-full max-w-5xl items-center gap-5 sm:grid-cols-[1.1fr_1fr] sm:gap-6 lg:grid-cols-[1.2fr_1fr] lg:gap-10">
          <MediaPreview preview={preview} displayName={trimmed || 'You'} tone="dark" />

          {/* The `max-height` variants are the only place in this feature keyed
              on viewport HEIGHT, and they are keyed on it because that is
              genuinely the constraint: a phone in landscape is 390px tall and
              wide enough to clear every width breakpoint there is. They buy
              back the last ~34px, which is what this column is over by there. */}
          <form onSubmit={submit} className="space-y-4 [@media(max-height:430px)]:space-y-3 sm:space-y-6">
            <div className="space-y-1.5 sm:space-y-2">
              <p className="text-[0.6875rem] font-semibold tracking-[0.1em] text-white/35 uppercase [@media(max-height:430px)]:hidden">
                Joining
              </p>
              {/* The type scale steps down rather than off: this is the title of
                  the room you are about to enter and it has to survive being
                  the first thing read on a phone. */}
              <h1 className="display text-2xl text-white sm:text-3xl">
                {title ?? 'A call is waiting.'}
              </h1>
              <p className="text-sm text-white/45">
                {host ? <>{host} is expecting you · </> : null}
                Code <span className="code-display text-white/75">{formatJoinCode(code)}</span>
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="prejoin-name"
                className="block text-[0.8125rem] font-medium text-white/60"
              >
                Your name
              </label>
              <input
                id="prejoin-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Ana Ruiz"
                autoComplete="name"
                /* Focusing this on a phone opens the keyboard immediately,
                   which covers the bottom half of the screen — including the
                   camera preview this screen exists to show you and the button
                   it exists to get you to press. On a desktop the caret lands
                   where it should and costs nothing. */
                autoFocus={!touch}
                /* Names are capitalised and the form has exactly one field, so
                   the keyboard should say "go", not "return". */
                autoCapitalize="words"
                enterKeyHint="go"
                maxLength={64}
                className="h-12 w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 text-base text-white placeholder:text-white/25 transition-colors focus:border-[#a690ff]/60 focus:bg-white/[0.06] focus:outline-none"
              />
              {/* True, and worth saying — but it is 48px of policy above the
                  join button on a 667px screen, and the phone is where somebody
                  is joining from a link with one thumb. */}
              <p className="hidden text-xs text-white/30 sm:block">
                Shown to the others. It is never used as an identifier — the platform mints an
                opaque one for that, because identities end up in storage keys that cannot be
                rewritten later.
              </p>
            </div>

            {error ? (
              <div className="rounded-xl border border-[#f2685f]/30 bg-[#2c1615]/70 p-4">
                <p className="text-[0.8125rem] font-medium text-[#f2685f]">{error.title}</p>
                <p className="mt-1 text-[0.8125rem] leading-relaxed text-white/50">{error.detail}</p>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={!canJoin}
              className={cn(
                'inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-medium transition-[filter,opacity]',
                'bg-[#a690ff] text-[#150f2b] hover:brightness-110 active:brightness-95 disabled:opacity-40',
              )}
            >
              {busy ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Joining…
                </>
              ) : (
                <>
                  Join the call
                  <ArrowRight className="size-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
